import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Alert, Badge } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import { Slideout } from '../slideout';
import LoadingSpinner from '../loading-spinner';
import ErrorMessage from '../error-message';
import { graphqlErrorsToUiErrors } from '../../api';
import { GET_API_KEYS_BY_TEAM, GET_TEAM_NAME } from '../../api/queries/apiKeys';
import { CREATE_API_KEY, REVOKE_API_KEY } from '../../api/mutations/apiKeys';
import styles from './ApiKeysSlideout.module.scss';

interface ApiKeyRow {
  id: string;
  name: string;
  keyPrefix: string;
  status: string;
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
  revokedAt?: string | null;
  requestCount: number;
  createdByName?: string | null;
}

interface ApiKeysSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
}

type View = 'list' | 'create' | 'reveal';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const statusVariant = (status: string): string => {
  if (status === 'active') return 'success';
  if (status === 'revoked') return 'danger';
  return 'secondary';
};

const ApiKeysSlideout: React.FC<ApiKeysSlideoutProps> = ({ isOpen, onClose, teamId }) => {
  const { formatMessage } = useIntl();

  const [view, setView] = useState<View>('list');
  const [keyName, setKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_API_KEYS_BY_TEAM, {
    variables: { teamId },
    skip: !teamId || !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  // Fetched so we can show the user exactly which team the new key will
  // authenticate against — important for admins of multiple teams.
  const { data: teamData } = useQuery(GET_TEAM_NAME, {
    variables: { teamId },
    skip: !teamId || !isOpen,
    fetchPolicy: 'cache-first',
  });
  const teamName: string | undefined = teamData?.teamById?.name;

  const [createApiKey, { loading: creating }] = useMutation(CREATE_API_KEY);
  const [revokeApiKey, { loading: revoking }] = useMutation(REVOKE_API_KEY);

  const keys: ApiKeyRow[] = data?.apiKeysByTeam || [];

  const resetAndClose = () => {
    setView('list');
    setKeyName('');
    setRevealedKey(null);
    setPendingRevokeId(null);
    setCopied(false);
    onClose();
  };

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    const result = await createApiKey({ variables: { teamId, name: keyName.trim() } });
    const raw = result.data?.createApiKey?.rawKey;
    if (raw) {
      setRevealedKey(raw);
      setView('reveal');
      setKeyName('');
      refetch();
    }
  };

  const handleRevoke = async (id: string) => {
    await revokeApiKey({ variables: { apiKeyId: id } });
    setPendingRevokeId(null);
    refetch();
  };

  const handleCopy = () => {
    if (!revealedKey) return;
    navigator.clipboard.writeText(revealedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const goBackToList = () => {
    setView('list');
    setRevealedKey(null);
    setKeyName('');
    setCopied(false);
  };

  const renderHeader = () => {
    if (view === 'create') {
      return <FormattedMessage id="api-keys.create.title" defaultMessage="Create API Key" />;
    }
    if (view === 'reveal') {
      return <FormattedMessage id="api-keys.reveal.title" defaultMessage="Save your API key" />;
    }
    return <FormattedMessage id="title.api-keys" defaultMessage="API Keys" />;
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={resetAndClose}
      width="medium"
      title={renderHeader()}
      disableOutsideClick={view === 'reveal'}
    >
      <div className={styles.container}>
        {view === 'list' && (
          <>
            <div className={styles.toolbar}>
              <Button variant="primary" onClick={() => setView('create')}>
                <FormattedMessage id="api-keys.create" defaultMessage="Create API Key" />
              </Button>
            </div>

            {loading && !data && <LoadingSpinner />}

            {error && (
              <ErrorMessage>{graphqlErrorsToUiErrors(error)._general}</ErrorMessage>
            )}

            {!loading && keys.length === 0 && (
              <div className={styles.empty}>
                <h5 className="text-muted mb-2">
                  <FormattedMessage id="api-keys.empty.title" defaultMessage="No API keys yet" />
                </h5>
                <p className="text-muted small mb-0">
                  <FormattedMessage
                    id="api-keys.empty.description"
                    defaultMessage='Click "Create API Key" to issue your first key. The full key is shown only once — store it securely.'
                  />
                </p>
              </div>
            )}

            {keys.length > 0 && (
              <div className={styles.list}>
                {keys.map((k) => (
                  <div key={k.id} className={styles.row}>
                    <div className={styles.rowMain}>
                      <div className={styles.rowName}>
                        <strong>{k.name}</strong>
                        <Badge variant={statusVariant(k.status)} className="ml-2">{k.status}</Badge>
                      </div>
                      <div className={styles.rowMeta}>
                        <code>{k.keyPrefix}…</code>
                        {k.createdByName && <span> · by {k.createdByName}</span>}
                        <span> · created {formatDate(k.createdAt)}</span>
                      </div>
                      <div className={styles.rowStats}>
                        <span>
                          <FormattedMessage id="api-keys.last-used" defaultMessage="Last used" />: {formatDate(k.lastUsedAt)}
                        </span>
                        <span>
                          · <FormattedMessage id="api-keys.requests" defaultMessage="Requests" />: {k.requestCount}
                        </span>
                        {k.expiresAt && (
                          <span> · <FormattedMessage id="api-keys.expires" defaultMessage="Expires" />: {formatDate(k.expiresAt)}</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.rowActions}>
                      {k.status === 'active' && pendingRevokeId !== k.id && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setPendingRevokeId(k.id)}
                        >
                          <FormattedMessage id="api-keys.revoke" defaultMessage="Revoke" />
                        </Button>
                      )}
                      {pendingRevokeId === k.id && (
                        <div className={styles.confirmInline}>
                          <span className={styles.confirmText}>
                            <FormattedMessage id="api-keys.revoke.confirm-short" defaultMessage="Revoke?" />
                          </span>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => setPendingRevokeId(null)}
                            disabled={revoking}
                          >
                            <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleRevoke(k.id)}
                            disabled={revoking}
                          >
                            <FormattedMessage id="api-keys.revoke.yes" defaultMessage="Yes, revoke" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {view === 'create' && (
          <div className={styles.panel}>
            {teamName && (
              <div className={styles.teamLabel}>
                <FormattedMessage
                  id="api-keys.create.for-team"
                  defaultMessage="Creating key for workspace: {teamName}"
                  values={{ teamName: <strong>{teamName}</strong> }}
                />
              </div>
            )}
            <Form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage id="api-keys.create.name-label" defaultMessage="Key name" />
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={formatMessage({ id: 'api-keys.create.name-placeholder', defaultMessage: 'e.g. Crexi Integration' })}
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  autoFocus
                />
                <Form.Text className="text-muted">
                  <FormattedMessage id="api-keys.create.name-help" defaultMessage="A friendly label to identify where this key is used." />
                </Form.Text>
              </Form.Group>

              <div className={styles.panelActions}>
                <Button variant="link" onClick={goBackToList} disabled={creating}>
                  <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                </Button>
                <Button variant="primary" type="submit" disabled={creating || !keyName.trim()}>
                  {creating
                    ? <FormattedMessage id="cta.creating" defaultMessage="Creating…" />
                    : <FormattedMessage id="cta.create" defaultMessage="Create" />}
                </Button>
              </div>
            </Form>
          </div>
        )}

        {view === 'reveal' && revealedKey && (
          <div className={styles.panel}>
            <Alert variant="warning">
              <FormattedMessage
                id="api-keys.reveal.warning"
                defaultMessage="This is the only time your full API key will be shown. Copy it now and store it somewhere secure. You can revoke it at any time."
              />
            </Alert>
            <Form.Control
              as="textarea"
              value={revealedKey}
              readOnly
              rows={2}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
            <div className={styles.panelActions}>
              <Button variant={copied ? 'success' : 'secondary'} onClick={handleCopy}>
                {copied ? (
                  <FormattedMessage id="api-keys.reveal.copied" defaultMessage="Copied!" />
                ) : (
                  <FormattedMessage id="api-keys.reveal.copy" defaultMessage="Copy to clipboard" />
                )}
              </Button>
              <Button variant="primary" onClick={goBackToList}>
                <FormattedMessage id="api-keys.reveal.confirm" defaultMessage="I've saved this key" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Slideout>
  );
};

export default ApiKeysSlideout;
